/**
 * 플랜 회고. 스코어카드는 기준선·비용 EVM·CPM을 다시 계산하고,
 * KPT 투표는 추가만 된다. 템플릿은 WBS와 의존성만 가져간다.
 */
import { WorkCalendar } from "@/engine/calendar";
import { computeSchedule } from "@/engine/cpm";
import { diffDays, isISODate, type ISODate } from "@/engine/date";
import { computeEvm, createBaseline } from "@/engine/health";
import type { Dependency, KptCard, Project, ProjectRetrospective, Task } from "@/engine/types";
import { costEvmOf } from "@/features/pm/cost-evm";
import { activeBaselineOf } from "@/features/reports/report-model";
import { fmtCurrency } from "@/lib/format";
import { recordPhase } from "@/store/record-ops";

export const RETRO_UNAVAILABLE = "판정 불가";

export type RetroPhase = "active" | "done" | "archived";
export type RetroAccess = "draft" | "edit" | "readonly";
export type KptColumn = KptCard["column"];

export interface RetroMetric {
  id: string;
  label: string;
  text: string;
  unavailable: boolean;
}

export interface RetroScorecard {
  metrics: RetroMetric[];
}

export interface ProjectTemplate {
  id: string;
  name: string;
  sourceProjectId: string;
  sourceProjectName: string;
  createdAt: string;
  tasks: Task[];
  dependencies: Dependency[];
}

export function retroPhase(project: Pick<Project, "lifecycle" | "statusOverride">): RetroPhase {
  if (project.lifecycle === "archived" || project.lifecycle === "trashed") return "archived";
  if (project.lifecycle === "done" || project.statusOverride === "done") return "done";
  return "active";
}

/** 활성은 초안, 완료는 수정, 보관은 읽기. */
export function retroAccess(phase: RetroPhase): RetroAccess {
  if (phase === "archived") return "readonly";
  if (phase === "done") return "edit";
  return "draft";
}

function metric(id: string, label: string, text: string | null): RetroMetric {
  if (text == null) return { id, label, text: RETRO_UNAVAILABLE, unavailable: true };
  return { id, label, text, unavailable: false };
}

function signedDays(days: number): string {
  if (days === 0) return "0일";
  return `${days > 0 ? "+" : ""}${days}일`;
}

function dayOf(value: string | undefined): ISODate | null {
  if (!value) return null;
  const day = value.slice(0, 10);
  return isISODate(day) ? day : null;
}

function leafCount(project: Project): number {
  const parentIds = new Set(project.tasks.map((task) => task.parentId).filter((id): id is string => !!id));
  return project.tasks.filter((task) => !parentIds.has(task.id)).length;
}

/** 종결된 이슈·리스크의 등록~종결 평균 일수. 종결 시각이 없으면 빠진다. */
export function meanTimeToResolveDays(project: Pick<Project, "records">): number | null {
  const samples: number[] = [];
  for (const record of project.records ?? []) {
    if (record.kind !== "issue" && record.kind !== "risk") continue;
    if (recordPhase(record.status) !== "closed") continue;
    const opened = dayOf(record.createdAt);
    const closed = dayOf(record.closedAt);
    if (!opened || !closed) continue;
    samples.push(Math.max(0, diffDays(opened, closed)));
  }
  if (!samples.length) return null;
  const mean = samples.reduce((sum, days) => sum + days, 0) / samples.length;
  return Math.round(mean * 10) / 10;
}

export function buildScorecard(project: Project): RetroScorecard {
  const calendar = new WorkCalendar(project.calendar);
  const schedule = computeSchedule(project, calendar);
  const baseline = activeBaselineOf(project);
  const evm = computeEvm(project, schedule, baseline, calendar);
  const earned = (() => {
    const leaves = schedule.order.map((id) => schedule.tasks.get(id)!).filter((task) => !task.isSummary);
    if (!leaves.length) return 0;
    return leaves.reduce((sum, task) => sum + task.percentComplete, 0) / leaves.length;
  })();
  const cost = costEvmOf(project, evm, earned);
  const costReady = cost.available && cost.bac > 0;

  let milestoneText: string | null = null;
  if (evm.hasBaseline && baseline) {
    const milestones = schedule.order
      .map((id) => schedule.tasks.get(id)!)
      .filter((task) => task.isMilestone && !task.isSummary && baseline.tasks[task.id]);
    if (milestones.length) {
      const onTime = milestones.filter((task) => task.finish <= baseline.tasks[task.id]!.finish).length;
      milestoneText = `${Math.round((onTime / milestones.length) * 100)}%`;
    }
  }

  const mttr = meanTimeToResolveDays(project);
  const issues = (project.records ?? []).filter((record) => record.kind === "issue").length;

  return {
    metrics: [
      metric("finishVariance", "종료 편차", evm.hasBaseline ? signedDays(evm.finishVarianceDays) : null),
      metric("spi", "SPI", evm.hasBaseline && evm.spi != null ? evm.spi.toFixed(2) : null),
      metric("milestone", "마일스톤 준수", milestoneText),
      metric("bac", "BAC", costReady ? fmtCurrency(cost.bac) : null),
      metric("ac", "AC", costReady && cost.ac != null ? fmtCurrency(cost.ac) : null),
      metric("cv", "CV", costReady && cost.cv != null ? fmtCurrency(cost.cv) : null),
      metric("cpi", "CPI", costReady && cost.cpi != null ? cost.cpi.toFixed(2) : null),
      metric("wbs", "WBS", String(leafCount(project))),
      metric("tasks", "Task", String(project.executionTasks?.length ?? 0)),
      metric("issues", "이슈", String(issues)),
      metric("mttr", "MTTR", mttr == null ? null : `${mttr}일`),
    ],
  };
}

function cleanBody(value: unknown): string {
  return typeof value === "string" ? value.replace(/\s+/g, " ").trim().slice(0, 500) : "";
}

function isColumn(value: unknown): value is KptColumn {
  return value === "keep" || value === "problem" || value === "try";
}

export function normalizeKptCard(raw: unknown): KptCard | null {
  const card = raw as Partial<KptCard> | null;
  if (!card || typeof card.id !== "string" || !card.id.trim() || !isColumn(card.column)) return null;
  const body = cleanBody(card.body);
  if (!body) return null;
  const votes = Array.isArray(card.votes)
    ? [...new Set(card.votes.filter((id): id is string => typeof id === "string" && id.trim().length > 0))]
    : [];
  return {
    id: card.id,
    column: card.column,
    body,
    authorId: typeof card.authorId === "string" ? card.authorId.slice(0, 80) : "",
    authorName: typeof card.authorName === "string" && card.authorName.trim() ? card.authorName.trim().slice(0, 40) : "이름 없음",
    ...(card.anonymous ? { anonymous: true } : {}),
    votes,
    createdAt: typeof card.createdAt === "string" && card.createdAt ? card.createdAt : new Date(0).toISOString(),
  };
}

export function normalizeRetrospective(raw: unknown): ProjectRetrospective | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const value = raw as Partial<ProjectRetrospective>;
  const cards = Array.isArray(value.cards) ? value.cards.map(normalizeKptCard).filter((card): card is KptCard => !!card) : [];
  const summary = cleanBody(value.summary);
  if (!cards.length && !summary) return undefined;
  return { cards, ...(summary ? { summary } : {}) };
}

export function kptAuthorLabel(card: Pick<KptCard, "anonymous" | "authorName">): string {
  return card.anonymous ? "익명" : card.authorName || "이름 없음";
}

/** 새 카드의 투표는 비어 있다. */
export function addKptCard(cards: readonly KptCard[], draft: Omit<KptCard, "votes">): KptCard[] {
  const card = normalizeKptCard({ ...draft, votes: [] });
  if (!card) return [...cards];
  return [...cards, card];
}

/**
 * 한 표만 더한다. 같은 사람이 다시 누르면 배열을 그대로 돌려준다.
 * 표를 빼는 함수는 없다.
 */
export function upvoteKpt(cards: readonly KptCard[], cardId: string, voterId: string): readonly KptCard[] {
  const voter = voterId.trim();
  if (!voter) return cards;
  const index = cards.findIndex((card) => card.id === cardId);
  if (index < 0) return cards;
  const card = cards[index]!;
  if (card.votes.includes(voter)) return cards;
  const next = cards.slice();
  next[index] = { ...card, votes: [...card.votes, voter] };
  return next;
}

/** 표가 하나라도 있으면 본문을 바꾸지 않는다. */
export function updateKptBody(cards: readonly KptCard[], cardId: string, body: string): readonly KptCard[] {
  const index = cards.findIndex((card) => card.id === cardId);
  if (index < 0) return cards;
  const card = cards[index]!;
  if (card.votes.length > 0) return cards;
  const nextBody = cleanBody(body);
  if (!nextBody || nextBody === card.body) return cards;
  const next = cards.slice();
  next[index] = { ...card, body: nextBody };
  return next;
}

/** 표가 있는 Problem·Try 각 1장. 표가 같으면 먼저 만든 카드다. */
export function promotedLessons(cards: readonly KptCard[]): KptCard[] {
  const pick = (column: "problem" | "try"): KptCard | null => {
    const rows = cards.filter((card) => card.column === column && card.votes.length > 0);
    rows.sort((a, b) => b.votes.length - a.votes.length || a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
    return rows[0] ?? null;
  };
  return [pick("problem"), pick("try")].filter((card): card is KptCard => !!card);
}

export interface LessonRow {
  projectId: string;
  projectName: string;
  phase: "done" | "archived";
  spi: string;
  cpi: string;
  finishVariance: string;
  lessonText: string;
}

function metricText(card: RetroScorecard, id: string): string {
  return card.metrics.find((row) => row.id === id)?.text ?? RETRO_UNAVAILABLE;
}

/** 완료·보관 플랜. 검색은 플랜 이름, PM 요약, KPT 본문이다. */
export function lessonRows(projects: readonly Project[], query = ""): LessonRow[] {
  const needle = query.trim().toLocaleLowerCase("ko");
  const rows: LessonRow[] = [];
  for (const project of projects) {
    if (project.lifecycle === "trashed") continue;
    const phase = retroPhase(project);
    if (phase === "active") continue;
    const retro = project.retrospective;
    const texts = (retro?.cards ?? []).map((card) => card.body);
    const hay = [project.name, retro?.summary ?? "", ...texts].join("\n").toLocaleLowerCase("ko");
    if (needle && !hay.includes(needle)) continue;
    const score = buildScorecard(project);
    rows.push({
      projectId: project.id,
      projectName: project.name,
      phase: phase === "archived" ? "archived" : "done",
      spi: metricText(score, "spi"),
      cpi: metricText(score, "cpi"),
      finishVariance: metricText(score, "finishVariance"),
      lessonText: texts.join(" · "),
    });
  }
  return rows.sort((a, b) => a.projectName.localeCompare(b.projectName, "ko") || a.projectId.localeCompare(b.projectId));
}

export function templateFromProject(project: Project, id: string, createdAt: string, name?: string): ProjectTemplate {
  const label = name?.trim() || `${project.name} 템플릿`;
  return {
    id,
    name: label.slice(0, 80),
    sourceProjectId: project.id,
    sourceProjectName: project.name,
    createdAt,
    tasks: project.tasks.map((task) => ({ ...task })),
    dependencies: project.dependencies.map((link) => ({ ...link })),
  };
}

/** 진척과 실제 원가는 비우고, WBS와 의존성만 새 플랜에 심는다. */
export function applyProjectTemplate(project: Project, template: ProjectTemplate): Project {
  return {
    ...project,
    tasks: template.tasks.map((task) => {
      const { actualCost: _drop, ...rest } = task;
      return { ...rest, percentComplete: 0 };
    }),
    dependencies: template.dependencies.map((link) => ({ ...link })),
    baselines: [],
    activeBaselineId: null,
  };
}

export function normalizeProjectTemplate(raw: unknown): ProjectTemplate | null {
  const template = raw as Partial<ProjectTemplate> | null;
  if (!template || typeof template.id !== "string" || !template.id.trim()) return null;
  if (!Array.isArray(template.tasks) || template.tasks.length === 0) return null;
  const name = typeof template.name === "string" && template.name.trim() ? template.name.trim().slice(0, 80) : "템플릿";
  return {
    id: template.id,
    name,
    sourceProjectId: typeof template.sourceProjectId === "string" ? template.sourceProjectId : "",
    sourceProjectName: typeof template.sourceProjectName === "string" ? template.sourceProjectName : "",
    createdAt: typeof template.createdAt === "string" && template.createdAt ? template.createdAt : new Date(0).toISOString(),
    tasks: template.tasks.map((task) => ({ ...task })),
    dependencies: Array.isArray(template.dependencies) ? template.dependencies.map((link) => ({ ...link })) : [],
  };
}

export function normalizeProjectTemplates(raw: unknown): ProjectTemplate[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeProjectTemplate).filter((template): template is ProjectTemplate => !!template);
}

/** 테스트와 시드가 기준선을 만들 때 쓴다. 화면은 기존 기준선 저장을 쓴다. */
export function withFreshBaseline(project: Project, name = "착수"): Project {
  const schedule = computeSchedule(project, new WorkCalendar(project.calendar));
  const baseline = createBaseline(project, schedule, name);
  return { ...project, baselines: [...project.baselines, baseline], activeBaselineId: baseline.id };
}
